import {
  VisionaryScene,
  VisionaryObject,
} from "@/visionaryControllers/visionaryScene";
import { AnimationController } from "@/visionaryControllers/animationController";
// import { SceneController } from "@/visionaryControllers/sceneController"
import { ViewHelperController } from "@/visionaryControllers/viewhelperController";
import { CameraController } from "@/visionaryControllers/cameraController";
import { Camera } from "three";
import { ModelDialogTools } from "@/uiControls/model-dialog-tools";
import { FileController } from "@/visionaryControllers/fileController";
import { SceneFS } from "visionary-core";

/**
 * 场景信息，包括VisionaryScene、AnimationController、SceneController、viewhelplerController
 *
 */
export interface visionarySceneInfo {
  scene: VisionaryScene;
  animationController: AnimationController;
  cameraController: CameraController;
  fileController: FileController;
  viewHelperController: ViewHelperController;
}

export type ViewWindow = "left" | "right";

export interface SkyboxPathOption {
  value: string;
  label: string;
  i18nKey?: string;
}

const DEFAULT_SKYBOX_URL = "./public/textures/hdr/daytime.hdr";

// 导入类型（避免循环依赖，使用类型导入）
import type { MainViewTools } from "@/uiControls/main-view-tools";
import type { ToolbarNavTools } from "@/uiControls/toolbarNav-tools";
import type { RightPanelTools } from "@/uiControls/right-panel-tools";
import type { TimelinePanelTools } from "@/uiControls/panelControls/timeline-panel-tools";
import type { ExampleUtils } from "@/utils/exampleUtils";
import { MarbleImportTools } from "@/uiControls/panelControls/marble-import-tools";

export interface GlobalTools {
  cameraTools: any | null; // 相机控制器
  sceneController: any | null; // 场景控制器
  animationFrameTools: any | null; // 动画控制器
  viewHelperTools: any | null; // 坐标系SVG控制器
  visionaryCore: any | null; // VisionaryCore子模块集成
  themeController: any | null; // 主题控制器
  langTools: any | null; // 语言控制器
  toolbarNavTools: ToolbarNavTools | null; // 场景导航工具
  rightPanelTools: RightPanelTools | null; // 右侧工具栏
  timelinePanelTools: TimelinePanelTools | null; // 时间轴工具栏
  mainViewTools: MainViewTools | null; // 主视图工具
  marbleImportTools: MarbleImportTools | null; // Marble导入工具
  exampleUtils?: ExampleUtils | null; // 示例工具（可选）
}

export function initUIEventListener() {
  document.addEventListener("click", function (event) {
    var target = event.target;
    if (
      !target ||
      !(target as HTMLElement).classList.contains("marble-import-media-delete")
    ) {
      return;
    }
    var item = (target as HTMLElement).closest(".marble-import-media-item");
    if (!item) return;
    var mediaId = item.getAttribute("data-media-id");
    console.log("marble media delete clicked, data-media-id:", mediaId);
    if (modelDialogTools) {
      modelDialogTools.deleteMarbleMedia(mediaId ?? "");
    }
  });
}

const globalTools: GlobalTools = {
  cameraTools: null, // 相机控制器
  sceneController: null, // 场景控制器
  animationFrameTools: null, // 动画控制器
  viewHelperTools: null, // 坐标系SVG控制器
  visionaryCore: null, // VisionaryCore子模块集成
  themeController: null, // 主题控制器
  langTools: null, // 语言控制器
  toolbarNavTools: null, // 场景导航工具
  rightPanelTools: null, // 右侧工具栏
  timelinePanelTools: null, // 时间轴工具栏
  mainViewTools: null, // 主视图工具
  marbleImportTools: null, // Marble导入工具
};

// 各种初始状态，待细化 by helen 2025-10-30
const globalConfig = {
  // 从环境变量读取配置，开发环境使用 .env.development，生产环境使用 .env.production
  ROOT_PATH_URL:
    import.meta.env.VITE_ROOT_PATH_URL ||
    "https://visionary-laboratory.github.io/visionary",
  HOMEPAGE_PATH_URL:
    import.meta.env.VITE_HOMEPAGE_PATH_URL ||
    "https://visionary-laboratory.github.io/visionary", // 首页地址
  MARBLE_API_KEY: import.meta.env.VITE_MARBLE_API_KEY || "", // Marble API Key
  frameRate: 30,
  totalFrames: 120,
  confirmNoticeMessageDialog: false,
  mouseMovementMode: "orbit" as "orbit" | "fps",
  currentRenderMode: 0,
  heatmapParams: {
    blendBase: true,
    negRelevancy: true,
    opacity: 0.6,
    threshold: 0.0,
    tau01: 0.2,
    brightness01: 0.5,
    weightQuery64: null as Float32Array | null,
  },
  renderMode: {
    normal: 0, // 普通渲染
    pointcloud: 2, // 点云渲染 - 可以支持转化为点云的文件，需要扩展
    depth: 2, // 深度渲染
    normalmap: 1, // 法线贴图渲染
    pcaFeature: 3, // PCA特征
    heatmap: 4, // Heatmap (language similarity overlay)
  },
  modelParams: {
    // 模型参数 - 默认值
    position: { x: 0, y: 0, z: 0 }, // 位置
    rotation: { x: 0, y: 0, z: 0 }, // 旋转
    scale: { x: 1, y: 1, z: 1 }, // 缩放
    gaussianSize: 1, // 高斯大小
    gaussianOpacity: 1, // 高斯透明度
    maxShDeg: 3, // 球谐函数等级
    timeScale: 1, // 时间缩放
    timeOffset: 0, // 时间偏移
    maxPixelRadius: 512, // 最大像素半径
    isLoop: false, // 是否循环播放
  },
  recordingCameraParams: {
    // 录制相机参数 - 默认值
    width: 1920, // 宽度
    height: 1080, // 高度
    fov: 60, // FOV - 视野角
    nearPlane: 0.1, // 近平面
    farPlane: 1000, // 远平面
  },
  sceneParams: {
    // 场景参数 - 默认值
    grid: false, // 网格
    ort: "orbitView", // 控制球 或 第一人称 ortView或fpsView
    dragSpeed: 1, // 拖拽速度
    scaleSpeed: 1, // 缩放速度
    demping: 1, // 阻尼
    isAnimation: true, // 是否开启动画预览
    isPlaying: false, // 是否正在播放
  },
  cameraParams: {
    // 相机参数 - 默认值
    position: { x: 0.42, y: 0.73, z: 5 }, // 位置
    rotation: { x: 0, y: 0, z: 0 }, // 旋转
    scale: { x: 1, y: 1, z: 1 }, // 缩放
    fov: 60, // FOV - 视野角
    nearPlane: 0.1, // 近平面
    farPlane: 1000, // 远平面
    speed: 1, // 速度
  },
  domIds: {
    // mainAppContainer:'mainAppContainer',
    mainAppContainer: "visionaryEditorContainer",
    saveSceneButton: "saveSceneButtonItem",
    rightToolsBar: "rightPanelToolsBar",
  },

  videoSelectItems: {
    resolution: {
      // 分辨率
      value1920: { width: 1920, height: 1080 },
      value1280: { width: 1280, height: 720 },
      value3840: { width: 3840, height: 2160 },
      value2560: { width: 2560, height: 1440 },
      value960: { width: 960, height: 540 },
    },
    fps: {
      // 帧率
      v1: 1, // 测试用
      v10: 10,
      v24: 24,
      v30: 30,
      v60: 60,
    },
    bitrate: {
      // 比特率
      high: "high",
      medium: "medium",
      low: "low",
    },
  },
  lightSettings: {
    defalutSettings: {
      color: "#404040",
      ambientLight: {
        color: "#404040",
        intensity: 10,
      },
      skyboxSettings: {
        enabled: true,
        url: DEFAULT_SKYBOX_URL,
      },
    },
    ambientLight: {
      // 环境光
      color: "#404040",
      intensity: 10,
    },
    mainDirectionalLight: {
      // 主平行光源
      enabled: true,
      color: "#ffffff",
      position: { x: 10, y: 10, z: 5 },
      intensity: 0.9,
    },
    skyboxSettings: {
      // 天空盒设置
      enabled: true,
      url: DEFAULT_SKYBOX_URL,
    },
  },
};

class GlobalManager {
  private visionSceneMap: Map<string, visionarySceneInfo>;
  private currentSelected: { sceneid: string; objectid: string }; // 当前选中Scene、object的信息（id）
  private activePanelTab: string = "";
  private currentChangeCallback: (() => void) | null = null;
  private sceneFS: SceneFS | null = null;
  private injectedGetApp?: () => any;

  constructor() {
    this.visionSceneMap = new Map();
    this.currentSelected = { sceneid: "", objectid: "" };
    this.activePanelTab = "model"; // 默认激活模型面板
  }

  addSceneView(id: string, visionScene: visionarySceneInfo) {
    this.visionSceneMap.set(id, visionScene);
  }
  removeSceneViewById(id: string) {
    const sceneInfo = this.visionSceneMap.get(id);
    if (sceneInfo) {
      sceneInfo.scene.destroy();

      if (sceneInfo.cameraController) {
        sceneInfo.cameraController.destroy();
      }
      if (sceneInfo.animationController) {
        sceneInfo.animationController.destroy();
      }
      if (sceneInfo.fileController) {
        sceneInfo.fileController.destroy();
      }
      this.visionSceneMap.delete(id);
    }
  }
  getActivePanelTab() {
    return this.activePanelTab;
  }
  setActivePanelTab(tab: string) {
    this.activePanelTab = tab;
    window.dispatchEvent(
      new CustomEvent("event.panelTabChange", { detail: this.activePanelTab })
    );
  }
  getAllSceneInfos(): visionarySceneInfo[] {
    return Array.from(this.visionSceneMap.values());
  }

  setCurrentSelected(sceneid: string, objectid: string) {
    this.currentSelected.sceneid = sceneid;
    this.currentSelected.objectid = objectid;
    if (this.currentChangeCallback) {
      this.currentChangeCallback();
    }
    console.log("[globalManager] setCurrentSelected", this.currentSelected);
    window.dispatchEvent(
      new CustomEvent("event.modelSelectChange", {
        detail: this.currentSelected,
      })
    );
  }

  getCurrentSelected() {
    return this.currentSelected;
  }
  getCurrentSceneView(): VisionaryScene | null {
    const sceneInfo = this.visionSceneMap.get(this.currentSelected.sceneid);
    if (sceneInfo) {
      return sceneInfo.scene;
    }
    return null;
  }
  getCurrentSceneInfo(hasDefault: boolean = false): visionarySceneInfo | null {
    if (this.currentSelected.sceneid != "") {
      const sceneInfo = this.visionSceneMap.get(this.currentSelected.sceneid);
      if (sceneInfo) {
        return sceneInfo;
      }
    }
    if (!hasDefault) {
      return null;
    }
    if (this.visionSceneMap.size > 0) {
      const firstValue = this.visionSceneMap.values().next().value;
      return firstValue ?? null;
    }
    return null;
  }
  getCurrentObject(): VisionaryObject | null {
    const sceneInfo = this.visionSceneMap.get(this.currentSelected.sceneid);
    if (sceneInfo) {
      const obj = sceneInfo.scene.getObjectById(this.currentSelected.objectid);
      return obj ?? null;
    }
    return null;
  }
  getCurrentCamera(): CameraController | null {
    const sceneInfo = this.getCurrentSceneInfo();
    if (sceneInfo) {
      return sceneInfo.cameraController;
    }
    return null;
  }
  getCurrentAnimation(): AnimationController | null {
    const sceneInfo = this.getCurrentSceneInfo();
    if (sceneInfo) {
      return sceneInfo.animationController;
    }
    return null;
  }
  setCurrentChangeCallback(callback: () => void) {
    this.currentChangeCallback = callback;
  }

  getViewWindowIfSingle() {
    const mainAppContainerDom = document.getElementById(
      globalConfig.domIds.mainAppContainer
    );
    if (mainAppContainerDom) {
      return !mainAppContainerDom.classList.contains(
        "multi-window-app-container"
      );
    }
    return false;
  }

  openRightToolsBar() {
    const toolContainer = document.getElementById(
      globalConfig.domIds.rightToolsBar
    );
    if (toolContainer && !toolContainer.classList.contains("active")) {
      toolContainer.classList.add("active");
      window.dispatchEvent(new Event("resize"));
    }
  }
  closeRightToolsBar() {
    const toolContainer = document.getElementById(
      globalConfig.domIds.rightToolsBar
    );
    if (toolContainer && toolContainer.classList.contains("active")) {
      toolContainer.classList.remove("active");
      window.dispatchEvent(new Event("resize"));
    }
  }

  setSceneFS(sceneFS: SceneFS) {
    this.sceneFS = sceneFS;
  }
  getSceneFS() {
    return this.sceneFS;
  }
  setInjectedGetApp(getApp: () => any) {
    this.injectedGetApp = getApp;
  }
  getAppInstance(): any {
    if (this.injectedGetApp) return this.injectedGetApp();
    return (window as any).getAppForSceneFS
      ? (window as any).getAppForSceneFS()
      : null;
  }
}

// 创建全局GlobalManager实例
const globalManager = new GlobalManager();
// 创建全局模态框工具实例
const modelDialogTools = new ModelDialogTools();

export { globalConfig, globalManager, modelDialogTools, globalTools };
